import 'src/styles.css';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/nextjs';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Theme } from '@workos-inc/design-system';
import { WatchedContent } from '@workos-inc/docs-kit/lib/watched-content';
import { ClientError } from 'graphql-request';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { ThemeProvider } from 'next-themes';
import * as React from 'react';
import { CodeBlockLanguageProvider } from 'src/components/code-block-language-provider';
import { NavigationMobileProvider } from 'src/components/navigation-mobile';
import { AnalyticsProvider } from 'src/utils/analytics-context';
import { AuthProvider } from 'src/utils/auth-context';
import { usePageView } from 'src/utils/use-page-view';
const AppWithPageView = ({
  children
}: {
  children: React.ReactNode;
}) => {
  usePageView();
  return <>{children}</>;
};
const App = ({
  Component,
  pageProps
}: AppProps) => {
  const [queryClient] = React.useState(() => {
    const isProduction = process.env.NODE_ENV === 'production';
    return new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          retryOnMount: false,
          retry: (failureCount, error) => {
            if (!isProduction) {
              return false;
            }
            if (error instanceof ClientError) {
              const compareClientErrorRange = (statusCode: number) => statusCode >= 400 && statusCode < 500;
              let isWithinClientErrorRange = compareClientErrorRange(error.response.status);
              const errorExtensions = error.response.errors?.[0]?.extensions;
              if (errorExtensions && 'exception' in errorExtensions) {
                const statusCode: number = errorExtensions.exception.status ?? 200;
                isWithinClientErrorRange = compareClientErrorRange(statusCode);
              }
              if (isWithinClientErrorRange) {
                return false;
              }
              const isForbiddenResource = error.response.errors?.some(({
                message
              }) => message === 'Forbidden resource');
              if (isForbiddenResource) {
                return false;
              }
            }
            return failureCount <= 3;
          }
        }
      }
    });
  });
  const router = useRouter();
  const hubspotPortalId = process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID;
  return <>
      <WatchedContent port={3021} reload={router.reload} data-sentry-element="WatchedContent" data-sentry-source-file="_app.tsx" />
      <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-source-file="_app.tsx">
        <AuthProvider data-sentry-element="AuthProvider" data-sentry-source-file="_app.tsx">
          <SentryErrorBoundary data-sentry-element="SentryErrorBoundary" data-sentry-source-file="_app.tsx">
            <Theme data-sentry-element="Theme" data-sentry-source-file="_app.tsx">
              <AnalyticsProvider data-sentry-element="AnalyticsProvider" data-sentry-source-file="_app.tsx">
                <AppWithPageView data-sentry-element="AppWithPageView" data-sentry-source-file="_app.tsx">
                  <ThemeProvider disableTransitionOnChange attribute="class" defaultTheme="system" value={{
                  light: 'light-theme',
                  dark: 'dark-theme'
                }} data-sentry-element="ThemeProvider" data-sentry-source-file="_app.tsx">
                    <CodeBlockLanguageProvider data-sentry-element="CodeBlockLanguageProvider" data-sentry-source-file="_app.tsx">
                      <Head data-sentry-element="Head" data-sentry-source-file="_app.tsx">
                        <link href="https://images.workoscdn.com/brand/v2/favicon@32x32.png" rel="shortcut icon" type="image/x-icon" />
                        <link href="https://images.workoscdn.com/brand/v2/favicon@256x256.png" rel="apple-touch-icon" />
                      </Head>
                      {hubspotPortalId && <Script id="hs-script-loader" src={`https://js.hs-scripts.com/${hubspotPortalId}.js`} strategy="afterInteractive" />}
                      <NavigationMobileProvider data-sentry-element="NavigationMobileProvider" data-sentry-source-file="_app.tsx">
                        <Component {...pageProps} data-sentry-element="Component" data-sentry-source-file="_app.tsx" />

                        <ReactQueryDevtools buttonPosition="bottom-right" position="bottom" data-sentry-element="ReactQueryDevtools" data-sentry-source-file="_app.tsx" />
                      </NavigationMobileProvider>
                    </CodeBlockLanguageProvider>
                  </ThemeProvider>
                </AppWithPageView>
              </AnalyticsProvider>
            </Theme>
          </SentryErrorBoundary>
        </AuthProvider>
      </QueryClientProvider>
    </>;
};
export default withLDProvider({
  clientSideID: process.env.NEXT_PUBLIC_LAUNCH_DARKLY_CLIENT_SIDE_ID || '',
  reactOptions: {
    useCamelCaseFlagKeys: false
  }
  // Introducing lint rule banning type assertions
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
})(App as React.ComponentType);